import { Navigation } from '@Backlot-Cars/archie';
import { CreditAnnouncementModal } from 'components/CreditAnnouncementModal';
import { notifyError } from 'helpers/errorNotifier';
import { safeShutdownIntercom } from 'hooks/intercom/intercomClient';
import { formatMessage } from 'helpers/notification/formatMessage';
import { publicEnvConfig } from 'helpers/env';
import usePusher from 'hooks/usePusher';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ActivitiesService from 'services/navigation/activities.service';
import UserSessionService from 'services/navigation/userSession.service';
import OktaService from 'helpers/okta';

const isDevelopment = publicEnvConfig.RELEASE_STAGE === 'development';

export const handleLogout = async () => {
	try {
		await UserSessionService.instance.logout();
		await OktaService.getInstance().logout();
		safeShutdownIntercom();
		window.location.href = '/sign_in';
	} catch ({ error }) {
		notifyError(`Error to log out user - ${error?.message}`);
	}
};

const NavigationWrapper = ({ children, user }) => {
	const {
		notifications: activities,
		navigation,
		is_account_master,
		can_manage_users,
		auth,
		on_behalf_of,
		dealership_name,
		normal_image,
		title_clerk_token,
		number_of_pending_checkout_items,
		user_id,
		closed_marketplace,
		unread_activities,
		closed_marketplace_id,
		dealership_reps,
		username,
		messages,
		credit_announcements: creditAnnouncements,
	} = user;

	const userData = {
		id: user_id,
		navigation,
		is_account_master,
		can_manage_users,
		dealership_name,
		normal_image,
		title_clerk_token,
		on_behalf_of,
		closed_marketplace_id,
		dealership_reps,
		username,
	};

	const renderNav = Boolean(auth);

	const [notifications, setNotifications] = useState();
	const [unreadNotifications, setUnreadNotifications] = useState(unread_activities);
	const [loadingNotifications, setLoadingNotifications] = useState(false);

	const [loadingGroupAccounts, setLoadingGroupAccounts] = useState(false);
	const [groupAccounts, setGroupAccounts] = useState();

	const router = useRouter();

	usePusher(activities?.channel, activities?.events, (notification) => addNotification(notification), notifications);

	useEffect(() => {
		if (notifications) {
			const unread = notifications.filter((notification) => !notification.read);
			setUnreadNotifications(unread.length);
		}
	}, [notifications]);

	const parsedBanner = useMemo(
		() =>
			(messages || []).map((msg) => ({
				variant: msg.type,
				iconName: msg.icon_name,
				iconSize: msg.icon_size,
				title: msg.title,
				subtitle: msg.subtitle,
				buttons: (msg.buttons || []).map((button) => ({
					label: button.label,
					href: button.ref,
				})),
			})),
		[messages],
	);

	const handleCreditAnnouncementShown = useCallback((customerSelfServeCreditId) => {
		UserSessionService.instance.acknowledgeCreditAnnouncement(customerSelfServeCreditId).catch(() => {});
	}, []);

	if (!renderNav) return children;

	const handleSetNotifications = async () => {
		setLoadingNotifications(true);
		try {
			const { data } = await ActivitiesService.instance.getActivities(user);
			const activities = data.data?.activities.map(buildNotification);
			setNotifications(activities);
		} catch ({ error }) {
			notifyError(`Error getting user activities - ${error?.message}`);
		}
		setLoadingNotifications(false);
	};

	const handleSetGroupAccounts = async () => {
		setLoadingGroupAccounts(true);
		try {
			const { data } = await UserSessionService.instance.getGroupAccounts(user);
			const accounts = data?.data;
			setGroupAccounts(accounts);
		} catch ({ error }) {
			notifyError(`Error getting group accounts - ${error?.message}`);
		}
		setLoadingGroupAccounts(false);
	};

	const handleDeleteNotification = async (notificationId) => {
		try {
			const { data } = await ActivitiesService.instance.deleteActivity(user, notificationId);
			if (data?.success) {
				setNotifications((prevNotifications) =>
					prevNotifications.filter((notification) => notification.activity !== notificationId),
				);
			}
		} catch ({ error }) {
			notifyError(`Error deleting user activity ${notificationId} - ${error?.message}`);
		}
	};

	const handleDeleteAllNotifications = async () => {
		if (notifications?.length) {
			try {
				const { data } = await ActivitiesService.instance.deleteAllActivities(user);
				if (data?.success) {
					setNotifications([]);
				}
			} catch ({ error }) {
				notifyError(`Error deleting all user activities - ${error?.message}`);
			}
		}
	};

	const handleReadAllNotifications = async () => {
		try {
			ActivitiesService.instance.readAllActivities(user);
		} catch ({ error }) {
			notifyError(`Error on read all notifications - ${error?.message}`);
		}
	};

	const handleClearUnreadNotifications = () => {
		if (unreadNotifications > 0 && notifications) {
			const readNotifications = notifications.map((notification) => ({ ...notification, read: true }));
			setNotifications(readNotifications);
		}
	};

	const addNotification = (notification) => {
		if (notifications) {
			let newNotifications = [...notifications];
			newNotifications.unshift(buildNotification(notification));
			setNotifications(newNotifications);
		} else {
			setUnreadNotifications(unreadNotifications + 1);
		}
	};

	const buildNotification = (notification) => ({
		message: formatMessage(notification?.message),
		activity: notification?.activity,
		options: notification?.options,
		read: notification?.read,
	});

	const handleChangeAccount = async (id) => {
		try {
			const path = await UserSessionService.instance.changeAccount(id, user);
			router.push(path);
		} catch ({ error }) {
			notifyError(`Error changing account - ${error?.message}`);
		}
	};

	const handleNavigate = (path, target) => {
		if (target) {
			return window.open(path, target);
		}
		window.location.href = path;
	};

	return (
		<>
			<Navigation
				navigation={navigation}
				notifications={notifications}
				pendingCheckoutItems={number_of_pending_checkout_items}
				loadingGroupAccounts={loadingGroupAccounts}
				loadingNotifications={loadingNotifications}
				user={userData}
				unreadNotifications={unreadNotifications}
				groupAccounts={groupAccounts}
				closedMarketplace={closed_marketplace}
				handleDeleteNotification={handleDeleteNotification}
				handleDeleteAllNotifications={handleDeleteAllNotifications}
				handleReadAllNotifications={handleReadAllNotifications}
				handleClearUnreadNotifications={handleClearUnreadNotifications}
				handleNavigate={handleNavigate}
				handleLogout={handleLogout}
				handleSetNotifications={handleSetNotifications}
				handleSetGroupAccounts={handleSetGroupAccounts}
				handleChangeAccount={handleChangeAccount}
				banners={parsedBanner}
				routeConfig={{
					railsDevUrl: publicEnvConfig.RELATIVE_BASE_URL,
					isDevelopment: isDevelopment,
				}}
			>
				{children}
			</Navigation>
			<CreditAnnouncementModal
				creditAnnouncements={creditAnnouncements}
				onAnnouncementShown={handleCreditAnnouncementShown}
			/>
		</>
	);
};

export default NavigationWrapper;
